// src/pages/ConfirmationPage.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Alert,
  Box,
  Typography,
  Button,
  CircularProgress,
  Stack,
  Card,
  CardContent,
  Divider,
  Chip,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import IGD_wordmark_green from "../assets/IGD_wordmark_green.png";
import { getBookingInfo } from "../api/booking/booking";

/* ===== Storage keys (kept in sync with BookingPage) ===== */
const DRAFT_KEY = "igd_booking_draft";
const REMEMBER_KEY = "igd_booking_remember";
const REMEMBERED_FIELDS_KEY = "igd_booking_remembered_fields";

/* ===== Confirmation polling ===== */
const MAX_CONFIRMATION_ATTEMPTS = 10;
const CONFIRMATION_RETRY_MS = 1000;

/* ===== Time helpers (no timezone math) ===== */

function formatClock(hhmm) {
  const [hRaw, mRaw] = String(hhmm).split(":");
  const h = Math.max(0, Math.min(23, parseInt(hRaw, 10) || 0));
  const m = Math.max(0, Math.min(59, parseInt(mRaw, 10) || 0));
  const ampm = h >= 12 ? "PM" : "AM";
  const hour12 = h % 12 === 0 ? 12 : h % 12;

  return `${hour12}:${String(m).padStart(2, "0")} ${ampm}`;
}

function toRanges(csvOrArray) {
  const parts = Array.isArray(csvOrArray)
    ? csvOrArray
    : typeof csvOrArray === "string"
      ? csvOrArray.split(",")
      : [];

  const mins = [
    ...new Set(
      parts
        .map((s) => String(s).trim())
        .filter(Boolean)
        .map((s) => {
          const [h, m] = s.split(":").map(Number);
          return (h || 0) * 60 + (m || 0);
        })
    ),
  ].sort((a, b) => a - b);

  if (mins.length === 0) return [];

  const toHHMM = (mm) =>
    `${String(Math.floor(mm / 60)).padStart(2, "0")}:${String(
      mm % 60
    ).padStart(2, "0")}`;

  const ranges = [];
  let start = mins[0];
  let prev = mins[0];

  const flush = () => {
    if (start === prev) {
      ranges.push(formatClock(toHHMM(start)));
    } else {
      ranges.push(
        `${formatClock(toHHMM(start))} – ${formatClock(
          toHHMM(prev + 15)
        )}`
      );
    }
  };

  for (let i = 1; i < mins.length; i++) {
    if (mins[i] === prev + 15) {
      prev = mins[i];
    } else {
      flush();
      start = prev = mins[i];
    }
  }

  flush();
  return ranges;
}

function formatISODate(isoYmd) {
  const [y, m, d] = String(isoYmd).split("-").map(Number);

  if (!y || !m || !d) {
    return isoYmd || "";
  }

  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export default function ConfirmationPage() {
  const [loading, setLoading] = useState(true);
  const [bookingInfo, setBookingInfo] = useState(null);
  const [confirmationError, setConfirmationError] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();

  const sessionId = new URLSearchParams(location.search).get("session_id");

  useEffect(() => {
    let cancelled = false;

    if (!sessionId) {
      setConfirmationError(
        "We could not find your checkout session. Please contact Indy Golf Den if you completed payment."
      );
      setLoading(false);
      return undefined;
    }

    async function loadBookingInfo() {
      for (
        let attempt = 1;
        attempt <= MAX_CONFIRMATION_ATTEMPTS;
        attempt += 1
      ) {
        if (cancelled) return;

        try {
          const data = await getBookingInfo(sessionId);

          if (data?.status === "processing" || data?.status === "canceling") {
            if (attempt < MAX_CONFIRMATION_ATTEMPTS) {
              await wait(CONFIRMATION_RETRY_MS);
              continue;
            }

            throw new Error(
              "Your payment was received, but your booking is still being finalized. Please contact Indy Golf Den if you do not receive a confirmation email shortly."
            );
          }

          if (cancelled) return;

          setBookingInfo(data);
          setConfirmationError(null);

          sessionStorage.removeItem(DRAFT_KEY);
          void REMEMBER_KEY;
          void REMEMBERED_FIELDS_KEY;

          return;
        } catch (err) {
          if (cancelled) return;

          const isRefundedConflict =
            err?.status === 409 &&
            err?.data?.status === "refunded";

          if (isRefundedConflict) {
            setConfirmationError(
              err.data?.message ||
                "Another guest completed an overlapping reservation first. Your booking was canceled and your payment was refunded."
            );

            sessionStorage.removeItem(DRAFT_KEY);
            return;
          }

          const isStillProcessing =
            err?.status === 202 ||
            err?.data?.status === "processing" ||
            err?.data?.status === "canceling";

          if (
            isStillProcessing &&
            attempt < MAX_CONFIRMATION_ATTEMPTS
          ) {
            await wait(CONFIRMATION_RETRY_MS);
            continue;
          }

          console.error("Confirmation error:", err);

          setConfirmationError(
            err?.message ||
              "There was a problem confirming your booking. Please contact Indy Golf Den."
          );

          return;
        }
      }
    }

    loadBookingInfo().finally(() => {
      if (!cancelled) {
        setLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const attendeeCount = useMemo(() => {
    return Math.max(1, Number(bookingInfo?.attendees) || 1);
  }, [bookingInfo]);

  const prettyTimes = useMemo(() => {
    const raw =
      typeof bookingInfo?.time === "string"
        ? bookingInfo.time
        : Array.isArray(bookingInfo?.timeSlots)
          ? bookingInfo.timeSlots.join(",")
          : "";

    return toRanges(raw).join(", ");
  }, [bookingInfo]);

  const refCode = useMemo(() => {
    const id = bookingInfo?.id || bookingInfo?.bookingId || "";
    return id ? String(id).slice(-6).toUpperCase() : "";
  }, [bookingInfo]);

  if (loading) {
    return (
      <Box
        sx={{
          maxWidth: 720,
          mx: "auto",
          px: 2,
          py: 8,
          textAlign: "center",
        }}
      >
        <CircularProgress />
        <Typography mt={2}>Confirming your booking…</Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 1 }}
        >
          This may take a few seconds after payment.
        </Typography>
      </Box>
    );
  }

  if (confirmationError) {
    return (
      <Box sx={{ maxWidth: 720, mx: "auto", px: 2, py: 6 }}>
        <Box textAlign="center" mb={3}>
          <Box
            component="img"
            src={IGD_wordmark_green}
            alt="Indy Golf Den"
            sx={{ height: 76, width: "auto" }}
          />
        </Box>

        <Stack alignItems="center" spacing={2}>
          <ErrorOutlineIcon sx={{ fontSize: 48, color: "warning.main" }} />

          <Typography
            variant="h4"
            align="center"
            sx={{
              fontFamily: "lulo-one",
              color: "#193e2d",
              letterSpacing: "0.06em",
            }}
          >
            BOOKING UPDATE
          </Typography>

          <Alert severity="warning" sx={{ width: "100%" }}>
            {confirmationError}
          </Alert>

          <Typography align="center" color="text.secondary">
            If your booking was canceled because of a scheduling conflict,
            your payment will be returned to the original payment method.
            Refund timing depends on your bank or card issuer.
          </Typography>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            justifyContent="center"
            sx={{ mt: 2 }}
          >
            <Button
              variant="contained"
              onClick={() => navigate("/booking")}
              sx={{ minWidth: 200 }}
            >
              Choose Another Time
            </Button>

            <Button
              variant="outlined"
              onClick={() => navigate("/")}
              sx={{ minWidth: 200 }}
            >
              Return to Home
            </Button>
          </Stack>
        </Stack>
      </Box>
    );
  }

  if (!bookingInfo) {
    return (
      <Box
        sx={{
          maxWidth: 720,
          mx: "auto",
          px: 2,
          py: 8,
          textAlign: "center",
        }}
      >
        <Typography variant="h5" color="error" gutterBottom>
          Booking not found.
        </Typography>

        <Button variant="contained" onClick={() => navigate("/")}>
          Back to Home
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 720, mx: "auto", px: 2, py: 6 }}>
      <Box textAlign="center" mb={3}>
        <Box
          component="img"
          src={IGD_wordmark_green}
          alt="Indy Golf Den"
          sx={{ height: 76, width: "auto" }}
        />
      </Box>

      <Stack alignItems="center" spacing={1} sx={{ mb: 3 }}>
        <CheckCircleOutlineIcon
          sx={{ fontSize: 48, color: "#193e2d" }}
        />

        <Typography
          variant="h4"
          align="center"
          sx={{
            fontFamily: "lulo-one",
            color: "#193e2d",
            letterSpacing: "0.06em",
          }}
        >
          THANK YOU FOR BOOKING!
        </Typography>

        <Typography align="center" color="text.secondary">
          We look forward to seeing you soon.
        </Typography>

        {refCode && (
          <Chip
            label={`Reference: ${refCode}`}
            size="small"
            sx={{
              mt: 0.5,
              fontWeight: 600,
              bgcolor: "rgba(25,62,45,0.06)",
            }}
          />
        )}
      </Stack>

      <Card elevation={1} sx={{ borderRadius: 3, overflow: "hidden" }}>
        <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ mb: 1 }}
          >
            <CalendarMonthIcon
              fontSize="small"
              sx={{ color: "#193e2d" }}
            />

            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Booking Details
            </Typography>
          </Stack>

          <Stack spacing={0.5} sx={{ mb: 2 }}>
            <Typography variant="body1">
              <strong>Date:</strong>{" "}
              {formatISODate(bookingInfo.date)}
            </Typography>

            <Typography variant="body1">
              <strong>Time:</strong> {prettyTimes || "—"}
            </Typography>

            <Typography variant="body1">
              <strong>Name:</strong> {bookingInfo.firstName}{" "}
              {bookingInfo.lastName}
            </Typography>

            <Typography variant="body1">
              <strong>Phone:</strong> {bookingInfo.phone}
            </Typography>

            <Typography variant="body1">
              <strong>Email:</strong> {bookingInfo.email}
            </Typography>

            <Typography variant="body1">
              <strong>Attendees:</strong> {attendeeCount}
            </Typography>
          </Stack>

          <Divider sx={{ my: 2 }} />

          <Typography variant="body2" color="text.secondary">
            A confirmation email should arrive shortly. If you have any
            questions about your reservation, please contact Indy Golf Den
            and reference your booking code above.
          </Typography>

          <br />

          <Typography variant="body2" color="text.secondary">
            MANDATORY: Every guest MUST sign a waiver immediately upon
            entering the facility. As soon as you walk in the door, there
            will be a small table in front of you with either an iPad to
            sign a waiver, or a physical copy. If iPad is not present,
            please sign the physical copy, and text a picture of the
            signed page to (317) 983-2224.
          </Typography>
        </CardContent>
      </Card>

      <Stack
        direction="row"
        spacing={2}
        justifyContent="center"
        sx={{ mt: 4 }}
      >
        <Button
          variant="contained"
          onClick={() => navigate("/")}
          sx={{ minWidth: 220 }}
        >
          Return to Home
        </Button>
      </Stack>
    </Box>
  );
}